import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// Color palette
const COLORS = {
  primary: [79, 70, 229],      // indigo-600
  primaryDark: [55, 48, 163],   // indigo-800
  primaryLight: [238, 242, 255], // indigo-50
  accent: [59, 130, 246],       // blue-500
  success: [16, 185, 129],      // emerald-500
  warning: [245, 158, 11],      // amber-500
  danger: [239, 68, 68],        // red-500
  dark: [15, 23, 42],           // slate-900
  medium: [100, 116, 139],      // slate-500
  light: [241, 245, 249],       // slate-100
  white: [255, 255, 255],
  gold: [234, 179, 8],
  silver: [148, 163, 184],
  bronze: [180, 83, 9],
};

const addHeader = (doc, examName) => {
  const pageW = doc.internal.pageSize.getWidth();

  // Top gradient bar
  doc.setFillColor(...COLORS.primary);
  doc.rect(0, 0, pageW, 44, "F");
  doc.setFillColor(...COLORS.primaryDark);
  doc.rect(0, 0, pageW, 4, "F");

  // Logo circle
  doc.setFillColor(255, 255, 255, 0.2);
  doc.circle(28, 24, 12, "F");
  doc.setFillColor(...COLORS.white);
  doc.circle(28, 24, 8, "F");
  doc.setFillColor(...COLORS.primary);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(...COLORS.primary);
  doc.text("YL", 28, 26, { align: "center" });

  // Title
  doc.setTextColor(...COLORS.white);
  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.text("edSolutions360 | Examination Portal Results", 46, 20);

  // Subtitle - exam name
  doc.setFontSize(11);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(200, 210, 255);
  doc.text(examName, 46, 32);

  // Decorative line
  doc.setFillColor(...COLORS.accent);
  doc.rect(0, 44, pageW, 2, "F");

  return 54; // Return Y position after header
};

const addFooter = (doc, pageNum) => {
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  doc.setFillColor(...COLORS.light);
  doc.rect(0, pageH - 20, pageW, 20, "F");
  doc.setDrawColor(...COLORS.primary);
  doc.setLineWidth(0.5);
  doc.line(20, pageH - 20, pageW - 20, pageH - 20);

  doc.setFontSize(8);
  doc.setTextColor(...COLORS.medium);
  doc.setFont("helvetica", "normal");
  doc.text("edSolutions360 Placement Platform - Confidential", 20, pageH - 8);
  doc.text(`Generated: ${new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric", hour: "2-digit", minute: "2-digit" })}`, pageW / 2, pageH - 8, { align: "center" });
  doc.text(`Page ${pageNum}`, pageW - 20, pageH - 8, { align: "right" });
};

const addSectionTitle = (doc, title, y, icon) => {
  const pageW = doc.internal.pageSize.getWidth();
  doc.setFillColor(...COLORS.primaryLight);
  doc.roundedRect(20, y, pageW - 40, 14, 3, 3, "F");
  doc.setFillColor(...COLORS.primary);
  doc.roundedRect(20, y, 4, 14, 2, 2, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(...COLORS.primaryDark);
  doc.text(title, 30, y + 9.5);
  return y + 20;
};

const addStatBox = (doc, x, y, w, h, label, value, color) => {
  doc.setFillColor(...COLORS.white);
  doc.roundedRect(x, y, w, h, 3, 3, "F");
  doc.setDrawColor(...(color || COLORS.primary));
  doc.setLineWidth(0.8);
  doc.roundedRect(x, y, w, h, 3, 3, "S");

  // Top colored bar
  doc.setFillColor(...(color || COLORS.primary));
  doc.roundedRect(x, y, w, 3, 3, 3, "F");
  doc.rect(x, y + 1, w, 2, "F");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.setTextColor(...COLORS.dark);
  doc.text(String(value), x + w / 2, y + h / 2 + 2, { align: "center" });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...COLORS.medium);
  doc.text(label, x + w / 2, y + h - 5, { align: "center" });
};

// =====================================================
// EXAM MANAGEMENT REPORT (for leadership/management)
// =====================================================
export const generateExamReport = (exam, results, topicPerformance) => {
  const doc = new jsPDF("p", "mm", "a4");
  const pageW = doc.internal.pageSize.getWidth();
  let page = 1;

  // ---- PAGE 1: Executive Summary ----
  let y = addHeader(doc, exam.name);

  // Report metadata
  doc.setFillColor(...COLORS.light);
  doc.roundedRect(20, y, pageW - 40, 16, 3, 3, "F");
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.medium);
  doc.text(`Role: ${exam.role === "BA" ? "Business Analyst" : exam.role === "Frontend" ? "Frontend Developer" : "MERN Full Stack Developer"}`, 28, y + 7);
  doc.text(`Duration: ${exam.duration_minutes} minutes`, 120, y + 7);
  doc.text(`Questions: ${exam.num_questions}`, 28, y + 13);
  doc.text(`Report Date: ${new Date().toLocaleDateString()}`, 120, y + 13);
  y += 24;

  // Executive Summary Stats
  y = addSectionTitle(doc, "Executive Summary", y);

  const totalCandidates = results.length;
  const avgScore = totalCandidates > 0 ? Math.round(results.reduce((a, r) => a + (r.accuracy || 0), 0) / totalCandidates) : 0;
  const avgTime = totalCandidates > 0 ? Math.round(results.reduce((a, r) => a + (r.time_taken_seconds || 0), 0) / totalCandidates / 60) : 0;
  const passCount = results.filter(r => r.accuracy >= 60).length;
  const passRate = totalCandidates > 0 ? Math.round((passCount / totalCandidates) * 100) : 0;

  const boxW = (pageW - 60) / 4;
  addStatBox(doc, 20, y, boxW, 30, "Total Candidates", totalCandidates, COLORS.primary);
  addStatBox(doc, 20 + boxW + 5, y, boxW, 30, "Avg Accuracy", `${avgScore}%`, COLORS.accent);
  addStatBox(doc, 20 + 2 * (boxW + 5), y, boxW, 30, "Pass Rate", `${passRate}%`, COLORS.success);
  addStatBox(doc, 20 + 3 * (boxW + 5), y, boxW, 30, "Avg Time", `${avgTime}m`, COLORS.warning);
  y += 38;

  // ---- Top Candidates Ranking (Sorted by Best to Hire) ----
  y = addSectionTitle(doc, "Candidate Rankings - Best to Hire", y);

  if (results.length > 0) {
    // Sort by rank (best first)
    const sortedResults = [...results].sort((a, b) => (a.rank || 999) - (b.rank || 999));

    const tableData = sortedResults.map((r) => {
      const hardData = r.difficulty_breakdown?.hard || { total: 0, correct: 0 };
      const hardAcc = hardData.total > 0 ? Math.round((hardData.correct / hardData.total) * 100) : 0;
      const timeStr = `${Math.floor(r.time_taken_seconds / 60)}m ${r.time_taken_seconds % 60}s`;
      const recommendation = r.recommendation || (r.accuracy >= 80 ? "Strongly Recommended" : r.accuracy >= 65 ? "Recommended" : r.accuracy >= 50 ? "Consider" : "Not Recommended");

      return [
        `#${r.rank}`,
        r.candidate_name,
        r.candidate_email,
        r.score,
        `${r.accuracy}%`,
        `${hardAcc}%`,
        timeStr,
        recommendation
      ];
    });

    autoTable(doc, {
      startY: y,
      head: [["Rank", "Name", "Email", "Score", "Accuracy", "Hard Q", "Time", "Recommendation"]],
      body: tableData,
      margin: { left: 20, right: 20 },
      styles: {
        fontSize: 8,
        cellPadding: 3,
        lineColor: [226, 232, 240],
        lineWidth: 0.3,
      },
      headStyles: {
        fillColor: COLORS.primary,
        textColor: COLORS.white,
        fontStyle: "bold",
        fontSize: 8,
      },
      alternateRowStyles: {
        fillColor: [248, 250, 252],
      },
      columnStyles: {
        0: { halign: "center", cellWidth: 14, fontStyle: "bold" },
        3: { halign: "center", cellWidth: 14 },
        4: { halign: "center", cellWidth: 18 },
        5: { halign: "center", cellWidth: 16 },
        6: { halign: "center", cellWidth: 18 },
        7: { cellWidth: 30 },
      },
      didParseCell: (data) => {
        if (data.section === "body" && data.column.index === 7) {
          const val = data.cell.raw;
          if (val === "Strongly Recommended") data.cell.styles.textColor = COLORS.success;
          else if (val === "Recommended") data.cell.styles.textColor = COLORS.accent;
          else if (val === "Consider") data.cell.styles.textColor = COLORS.warning;
          else data.cell.styles.textColor = COLORS.danger;
          data.cell.styles.fontStyle = "bold";
        }
        if (data.section === "body" && data.column.index === 0) {
          const rank = parseInt(data.cell.raw.replace("#", ""));
          if (rank === 1) data.cell.styles.textColor = COLORS.gold;
          else if (rank === 2) data.cell.styles.textColor = COLORS.silver;
          else if (rank === 3) data.cell.styles.textColor = COLORS.bronze;
        }
      },
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  // ---- Topic Performance ----
  if (topicPerformance && topicPerformance.length > 0) {
    if (y > 220) {
      addFooter(doc, page);
      doc.addPage();
      page++;
      y = addHeader(doc, exam.name);
    }

    y = addSectionTitle(doc, "Topic-wise Performance Analysis", y);

    const topicData = topicPerformance.map((t) => {
      const perf = t.accuracy >= 70 ? "Strong" : t.accuracy >= 50 ? "Average" : "Weak";
      return [t.topic, t.total_attempts, t.correct, `${t.accuracy}%`, t.easy, t.medium, t.hard, perf];
    });

    autoTable(doc, {
      startY: y,
      head: [["Topic", "Attempts", "Correct", "Accuracy", "Easy", "Medium", "Hard", "Performance"]],
      body: topicData,
      margin: { left: 20, right: 20 },
      styles: {
        fontSize: 8,
        cellPadding: 3,
        lineColor: [226, 232, 240],
        lineWidth: 0.3,
      },
      headStyles: {
        fillColor: COLORS.primaryDark,
        textColor: COLORS.white,
        fontStyle: "bold",
        fontSize: 8,
      },
      alternateRowStyles: {
        fillColor: [248, 250, 252],
      },
      columnStyles: {
        1: { halign: "center", cellWidth: 18 },
        2: { halign: "center", cellWidth: 16 },
        3: { halign: "center", cellWidth: 18 },
        4: { halign: "center", cellWidth: 14 },
        5: { halign: "center", cellWidth: 14 },
        6: { halign: "center", cellWidth: 14 },
        7: { cellWidth: 22 },
      },
      didParseCell: (data) => {
        if (data.section === "body" && data.column.index === 7) {
          const val = data.cell.raw;
          if (val === "Strong") data.cell.styles.textColor = COLORS.success;
          else if (val === "Average") data.cell.styles.textColor = COLORS.warning;
          else data.cell.styles.textColor = COLORS.danger;
          data.cell.styles.fontStyle = "bold";
        }
      },
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  // ---- Difficulty Analysis ----
  if (y > 230) {
    addFooter(doc, page);
    doc.addPage();
    page++;
    y = addHeader(doc, exam.name);
  }

  y = addSectionTitle(doc, "Difficulty Level Analysis", y);

  const diffData = { easy: { total: 0, correct: 0 }, medium: { total: 0, correct: 0 }, hard: { total: 0, correct: 0 } };
  results.forEach((r) => {
    if (r.difficulty_breakdown) {
      Object.entries(r.difficulty_breakdown).forEach(([diff, data]) => {
        if (diffData[diff]) {
          diffData[diff].total += data.total;
          diffData[diff].correct += data.correct;
        }
      });
    }
  });

  const diffBoxW = (pageW - 50) / 3;
  ["easy", "medium", "hard"].forEach((diff, i) => {
    const d = diffData[diff];
    const acc = d.total > 0 ? Math.round((d.correct / d.total) * 100) : 0;
    const x = 20 + i * (diffBoxW + 5);
    const color = diff === "easy" ? COLORS.success : diff === "medium" ? COLORS.warning : COLORS.danger;

    doc.setFillColor(...COLORS.white);
    doc.roundedRect(x, y, diffBoxW, 40, 3, 3, "F");
    doc.setDrawColor(...color);
    doc.setLineWidth(1);
    doc.roundedRect(x, y, diffBoxW, 40, 3, 3, "S");

    doc.setFillColor(...color);
    doc.roundedRect(x, y, diffBoxW, 5, 3, 3, "F");
    doc.rect(x, y + 3, diffBoxW, 2, "F");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(...COLORS.white);
    doc.text(diff.charAt(0).toUpperCase() + diff.slice(1), x + diffBoxW / 2, y + 4, { align: "center" });

    doc.setFontSize(22);
    doc.setTextColor(...color);
    doc.text(`${acc}%`, x + diffBoxW / 2, y + 22, { align: "center" });

    doc.setFontSize(8);
    doc.setTextColor(...COLORS.medium);
    doc.text(`${d.correct} / ${d.total} correct`, x + diffBoxW / 2, y + 32, { align: "center" });
  });
  y += 50;

  // ---- Key Insights ----
  if (y > 240) {
    addFooter(doc, page);
    doc.addPage();
    page++;
    y = addHeader(doc, exam.name);
  }

  y = addSectionTitle(doc, "Key Insights", y);

  const insights = [];
  if (results.length > 0) {
    const topCandidate = results[0];
    insights.push(`Top performer: ${topCandidate.candidate_name} with ${topCandidate.accuracy}% accuracy and score of ${topCandidate.score}`);
  }
  if (passRate >= 70) insights.push(`Strong pass rate of ${passRate}% indicates well-prepared candidates`);
  else if (passRate >= 40) insights.push(`Moderate pass rate of ${passRate}% - consider reviewing question difficulty`);
  else insights.push(`Low pass rate of ${passRate}% - candidates may need more preparation`);

  const strongTopics = (topicPerformance || []).filter(t => t.accuracy >= 70).map(t => t.topic);
  const weakTopics = (topicPerformance || []).filter(t => t.accuracy < 40).map(t => t.topic);
  if (strongTopics.length > 0) insights.push(`Strong areas: ${strongTopics.slice(0, 4).join(", ")}`);
  if (weakTopics.length > 0) insights.push(`Areas needing improvement: ${weakTopics.slice(0, 4).join(", ")}`);
  insights.push(`Average completion time: ${avgTime} minutes out of ${exam.duration_minutes} minutes allowed`);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.dark);
  insights.forEach((insight, i) => {
    doc.setFillColor(...(i % 2 === 0 ? COLORS.light : COLORS.white));
    doc.roundedRect(24, y, pageW - 48, 10, 2, 2, "F");
    doc.setFillColor(...COLORS.primary);
    doc.circle(30, y + 5, 1.5, "F");
    doc.text(insight, 36, y + 7);
    y += 12;
  });

  // Add footer to all pages
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    addFooter(doc, i);
  }

  doc.save(`edSolutions360_Exam_Report_${exam.name.replace(/\s+/g, "_")}.pdf`);
};

// =====================================================
// INDIVIDUAL CANDIDATE REPORT
// =====================================================
export const generateCandidateReport = (detail) => {
  const doc = new jsPDF("p", "mm", "a4");
  const pageW = doc.internal.pageSize.getWidth();
  let page = 1;

  // ---- PAGE 1: Candidate Overview ----
  let y = addHeader(doc, detail.exam_name);

  // Candidate Info Card
  doc.setFillColor(...COLORS.white);
  doc.roundedRect(20, y, pageW - 40, 32, 4, 4, "F");
  doc.setDrawColor(...COLORS.primary);
  doc.setLineWidth(0.5);
  doc.roundedRect(20, y, pageW - 40, 32, 4, 4, "S");

  // Avatar circle
  doc.setFillColor(...COLORS.primaryLight);
  doc.circle(38, y + 16, 10, "F");
  doc.setFillColor(...COLORS.primary);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.setTextColor(...COLORS.primary);
  const initials = detail.candidate_name.split(" ").map(n => n[0]).join("").substring(0, 2);
  doc.text(initials, 38, y + 19, { align: "center" });

  // Name and email
  doc.setTextColor(...COLORS.dark);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text(detail.candidate_name, 54, y + 12);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.medium);
  doc.text(detail.candidate_email, 54, y + 19);

  // Rank and status
  const rank = detail.rank || "-";
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.medium);
  doc.text(`Exam Date: ${detail.completed_at ? new Date(detail.completed_at).toLocaleDateString() : "N/A"}`, 54, y + 26);

  // Rank badge
  const rankColor = rank === 1 ? COLORS.gold : rank === 2 ? COLORS.silver : rank === 3 ? COLORS.bronze : COLORS.medium;
  doc.setFillColor(...rankColor);
  doc.roundedRect(pageW - 55, y + 6, 30, 20, 4, 4, "F");
  doc.setTextColor(...COLORS.white);
  doc.setFontSize(8);
  doc.text("RANK", pageW - 40, y + 13, { align: "center" });
  doc.setFontSize(16);
  doc.text(`#${rank}`, pageW - 40, y + 22, { align: "center" });

  y += 40;

  // Score Summary
  y = addSectionTitle(doc, "Performance Summary", y);

  const recommendation = detail.recommendation || (detail.accuracy >= 80 ? "Strongly Recommended" : detail.accuracy >= 65 ? "Recommended" : detail.accuracy >= 50 ? "Consider" : "Not Recommended");
  const recColor = recommendation === "Strongly Recommended" ? COLORS.success : recommendation === "Recommended" ? COLORS.accent : recommendation === "Consider" ? COLORS.warning : COLORS.danger;

  const boxW = (pageW - 60) / 5;
  addStatBox(doc, 20, y, boxW, 28, "Total Score", detail.score, COLORS.primary);
  addStatBox(doc, 20 + (boxW + 4), y, boxW, 28, "Accuracy", `${detail.accuracy}%`, COLORS.accent);
  addStatBox(doc, 20 + 2 * (boxW + 4), y, boxW, 28, "Correct", `${detail.correct_answers}/${detail.total_questions}`, COLORS.success);
  addStatBox(doc, 20 + 3 * (boxW + 4), y, boxW, 28, "Time Taken", `${Math.floor(detail.time_taken_seconds / 60)}m ${detail.time_taken_seconds % 60}s`, COLORS.warning);
  addStatBox(doc, 20 + 4 * (boxW + 4), y, boxW, 28, "Status", recommendation.split(" ")[0], recColor);
  y += 36;

  // Recommendation bar
  doc.setFillColor(...recColor);
  doc.roundedRect(20, y, pageW - 40, 12, 3, 3, "F");
  doc.setTextColor(...COLORS.white);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text(`Recommendation: ${recommendation}`, pageW / 2, y + 8, { align: "center" });
  y += 18;

  // Difficulty Breakdown
  y = addSectionTitle(doc, "Performance by Difficulty Level", y);

  const diffBreakdown = detail.difficulty_breakdown || {};
  const diffTableData = Object.entries(diffBreakdown).map(([diff, data]) => {
    const acc = data.total > 0 ? Math.round((data.correct / data.total) * 100) : 0;
    const weight = diff === "easy" ? 1 : diff === "medium" ? 2 : 3;
    const maxScore = data.total * weight;
    const earned = data.correct * weight;
    return [diff.charAt(0).toUpperCase() + diff.slice(1), data.total, data.correct, `${acc}%`, `x${weight}`, earned, maxScore];
  });

  autoTable(doc, {
    startY: y,
    head: [["Difficulty", "Questions", "Correct", "Accuracy", "Weight", "Points Earned", "Max Points"]],
    body: diffTableData,
    margin: { left: 20, right: 20 },
    styles: { fontSize: 9, cellPadding: 4, lineColor: [226, 232, 240], lineWidth: 0.3 },
    headStyles: { fillColor: COLORS.primary, textColor: COLORS.white, fontStyle: "bold" },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    columnStyles: {
      0: { fontStyle: "bold", cellWidth: 25 },
      1: { halign: "center" },
      2: { halign: "center" },
      3: { halign: "center" },
      4: { halign: "center" },
      5: { halign: "center" },
      6: { halign: "center" },
    },
    didParseCell: (data) => {
      if (data.section === "body" && data.column.index === 0) {
        const val = data.cell.raw;
        if (val === "Easy") data.cell.styles.textColor = COLORS.success;
        else if (val === "Medium") data.cell.styles.textColor = COLORS.warning;
        else data.cell.styles.textColor = COLORS.danger;
      }
    },
  });
  y = doc.lastAutoTable.finalY + 8;

  // ---- Topic Performance ----
  const topicMap = {};
  (detail.questions || []).forEach((q, i) => {
    const topic = q.topic || "Unknown";
    if (!topicMap[topic]) topicMap[topic] = { total: 0, correct: 0 };
    topicMap[topic].total++;
    const ans = detail.answers?.[String(i)];
    if (ans === q.correct_answer) topicMap[topic].correct++;
  });

  if (Object.keys(topicMap).length > 0) {
    if (y > 220) {
      addFooter(doc, page);
      doc.addPage();
      page++;
      y = addHeader(doc, detail.exam_name);
    }

    y = addSectionTitle(doc, "Topic-wise Performance", y);

    const topicTableData = Object.entries(topicMap)
      .sort((a, b) => (b[1].correct / b[1].total) - (a[1].correct / a[1].total))
      .map(([topic, data]) => {
        const acc = data.total > 0 ? Math.round((data.correct / data.total) * 100) : 0;
        const rating = acc >= 80 ? "Excellent" : acc >= 60 ? "Good" : acc >= 40 ? "Average" : "Weak";
        return [topic, data.total, data.correct, `${acc}%`, rating];
      });

    autoTable(doc, {
      startY: y,
      head: [["Topic", "Questions", "Correct", "Accuracy", "Rating"]],
      body: topicTableData,
      margin: { left: 20, right: 20 },
      styles: { fontSize: 9, cellPadding: 3.5, lineColor: [226, 232, 240], lineWidth: 0.3 },
      headStyles: { fillColor: COLORS.primaryDark, textColor: COLORS.white, fontStyle: "bold" },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      columnStyles: {
        1: { halign: "center", cellWidth: 22 },
        2: { halign: "center", cellWidth: 18 },
        3: { halign: "center", cellWidth: 20 },
        4: { cellWidth: 22 },
      },
      didParseCell: (data) => {
        if (data.section === "body" && data.column.index === 4) {
          const val = data.cell.raw;
          if (val === "Excellent") data.cell.styles.textColor = COLORS.success;
          else if (val === "Good") data.cell.styles.textColor = COLORS.accent;
          else if (val === "Average") data.cell.styles.textColor = COLORS.warning;
          else data.cell.styles.textColor = COLORS.danger;
          data.cell.styles.fontStyle = "bold";
        }
      },
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  // ---- Questions & Answers Detail ----
  addFooter(doc, page);
  doc.addPage();
  page++;
  y = addHeader(doc, detail.exam_name);

  y = addSectionTitle(doc, "Detailed Question-by-Question Analysis", y);

  (detail.questions || []).forEach((q, i) => {
    const candidateAnswer = detail.answers?.[String(i)];
    const isCorrect = candidateAnswer === q.correct_answer;
    const isSkipped = candidateAnswer === undefined || candidateAnswer === null;

    // Check if we need a new page
    if (y > 240) {
      addFooter(doc, page);
      doc.addPage();
      page++;
      y = addHeader(doc, detail.exam_name);
    }

    // Question box
    const statusColor = isCorrect ? COLORS.success : isSkipped ? COLORS.medium : COLORS.danger;
    const statusText = isCorrect ? "CORRECT" : isSkipped ? "SKIPPED" : "INCORRECT";
    const diffColor = q.difficulty === "easy" ? COLORS.success : q.difficulty === "medium" ? COLORS.warning : COLORS.danger;

    // Question header bar
    doc.setFillColor(...statusColor);
    doc.roundedRect(20, y, pageW - 40, 8, 2, 2, "F");
    doc.rect(20, y + 4, pageW - 40, 4, "F");

    doc.setTextColor(...COLORS.white);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.text(`Q${i + 1}`, 26, y + 5.5);

    // Difficulty badge
    doc.setFillColor(...diffColor);
    doc.roundedRect(36, y + 1.5, 16, 5, 1, 1, "F");
    doc.setFontSize(6);
    doc.text(q.difficulty.toUpperCase(), 44, y + 5, { align: "center" });

    // Topic
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.text(q.topic || "", 56, y + 5.5);

    // Status
    doc.text(statusText, pageW - 26, y + 5.5, { align: "right" });

    y += 10;

    // Question text
    doc.setFillColor(...COLORS.light);
    doc.roundedRect(20, y, pageW - 40, 0, 0, 0, "F");

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...COLORS.dark);
    const lines = doc.splitTextToSize(q.question_text, pageW - 48);
    const textHeight = lines.length * 4.5;
    doc.setFillColor(...COLORS.light);
    doc.rect(20, y, pageW - 40, textHeight + 4, "F");
    doc.text(lines, 24, y + 4);
    y += textHeight + 6;

    // Options
    (q.options || []).forEach((opt, idx) => {
      if (y > 268) {
        addFooter(doc, page);
        doc.addPage();
        page++;
        y = addHeader(doc, detail.exam_name);
      }

      const isThisCorrect = idx === q.correct_answer;
      const isThisSelected = idx === candidateAnswer;
      let optBg = COLORS.white;
      let optBorder = [226, 232, 240];
      let optText = COLORS.dark;

      if (isThisCorrect) {
        optBg = [236, 253, 245];
        optBorder = COLORS.success;
        optText = [6, 95, 70];
      } else if (isThisSelected && !isCorrect) {
        optBg = [254, 242, 242];
        optBorder = COLORS.danger;
        optText = [153, 27, 27];
      }

      doc.setFillColor(...optBg);
      doc.roundedRect(24, y, pageW - 48, 8, 2, 2, "F");
      doc.setDrawColor(...optBorder);
      doc.setLineWidth(0.4);
      doc.roundedRect(24, y, pageW - 48, 8, 2, 2, "S");

      // Option letter circle
      doc.setFillColor(...(isThisCorrect ? COLORS.success : isThisSelected ? COLORS.danger : [203, 213, 225]));
      doc.circle(31, y + 4, 3, "F");
      doc.setTextColor(...COLORS.white);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(7);
      doc.text(String.fromCharCode(65 + idx), 31, y + 5.5, { align: "center" });

      // Option text
      doc.setFont("helvetica", isThisCorrect || isThisSelected ? "bold" : "normal");
      doc.setFontSize(8);
      doc.setTextColor(...optText);
      const optLines = doc.splitTextToSize(opt, pageW - 70);
      doc.text(optLines[0], 37, y + 5.5);

      // Markers
      if (isThisCorrect) {
        doc.setTextColor(...COLORS.success);
        doc.setFontSize(7);
        doc.text("Correct Answer", pageW - 32, y + 5.5, { align: "right" });
      }
      if (isThisSelected && !isCorrect) {
        doc.setTextColor(...COLORS.danger);
        doc.setFontSize(7);
        doc.text("Selected", pageW - 32, y + 5.5, { align: "right" });
      }

      y += 10;
    });

    // Explanation
    if (q.explanation) {
      if (y > 262) {
        addFooter(doc, page);
        doc.addPage();
        page++;
        y = addHeader(doc, detail.exam_name);
      }
      doc.setFillColor(239, 246, 255);
      const explLines = doc.splitTextToSize(`Explanation: ${q.explanation}`, pageW - 56);
      const explH = explLines.length * 4 + 4;
      doc.roundedRect(24, y, pageW - 48, explH, 2, 2, "F");
      doc.setDrawColor(147, 197, 253);
      doc.setLineWidth(0.3);
      doc.roundedRect(24, y, pageW - 48, explH, 2, 2, "S");
      doc.setFillColor(59, 130, 246);
      doc.roundedRect(24, y, 3, explH, 2, 2, "F");
      doc.setTextColor(30, 64, 175);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.5);
      doc.text(explLines, 30, y + 4);
      y += explH + 4;
    }

    y += 4;
  });

  // Footers on all pages
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    addFooter(doc, i);
  }

  doc.save(`edSolutions360_${detail.candidate_name.replace(/\s+/g, "_")}_Report.pdf`);
};
